import { useSyncExternalStore } from "react";

/**
 * `SKIP_WAITING` in `src/sw/policy.ts`. Repeated, not imported: a module shared with the worker would
 * become a chunk `sw.js` imports, and the worker must stay one self-contained classic script.
 */
const SKIP_WAITING = "SKIP_WAITING";

/**
 * App updates on the Web SPA (R1-42, PRD §21). A new build's service worker installs in the background
 * and waits; `applyUpdate` activates it only after the safe checkpoint (no sale in flight, cart and
 * outbox persisted), then reloads into the new version. `stale`: a newer build in another tab upgraded
 * the local database, so this tab must reload before it can store anything again.
 */
export interface AppUpdateState {
  ready: boolean;
  stale: boolean;
}

let state: AppUpdateState = { ready: false, stale: false };
let waiting: ServiceWorker | null = null;
/** This tab asked the waiting build to activate (it reloads itself on `controllerchange`). */
let applying = false;
const listeners = new Set<() => void>();

function set(next: Partial<AppUpdateState>) {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function watch(registration: ServiceWorkerRegistration) {
  const offer = () => {
    // Only an update: the first install has no page it would replace.
    if (registration.waiting && navigator.serviceWorker.controller) {
      waiting = registration.waiting;
      set({ ready: true });
    }
  };
  offer();
  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    installing?.addEventListener("statechange", () => {
      if (installing.state === "installed") offer();
    });
  });
}

/** Registers `/sw.js` on a production web build; Tauri shells bundle their assets and skip it. */
export function registerServiceWorker(enabled: boolean): void {
  if (!enabled || !("serviceWorker" in navigator)) return;
  const hadController = Boolean(navigator.serviceWorker.controller);
  // Another tab applied the update: the new build now controls this tab too, but this page still runs
  // the old code. It reloads at its own safe checkpoint, like a stale tab.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController || applying) return;
    waiting = null;
    set({ ready: false, stale: true });
  });
  void navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((registration) => {
      watch(registration);
      // Long-running registers check for a new build now and then, not only on navigation.
      setInterval(() => void registration.update().catch(() => {}), 30 * 60 * 1000);
    })
    .catch((error: unknown) => console.warn("Service worker registration failed", error));
}

/** The local database was upgraded by a newer build in another tab. */
export function markStale(): void {
  set({ stale: true });
}

/**
 * Reaches the safe checkpoint, then activates the waiting build and reloads once it controls the page.
 * A stale tab just reloads after the checkpoint. Rejects (nothing activated) if it isn't safe.
 */
export async function applyUpdate(checkpoint: () => Promise<void>): Promise<void> {
  await checkpoint();
  if (!waiting) {
    window.location.reload();
    return;
  }
  navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), {
    once: true,
  });
  applying = true;
  waiting.postMessage({ type: SKIP_WAITING });
}

export function useAppUpdate(): AppUpdateState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
    () => state,
  );
}
